import { Link } from "react-router-dom";
import { SEO, serviceSchema, breadcrumbSchema } from "@/components/seo";
import { CTASection } from "@/components/common/CTASection";
import {
  FiTruck,
  FiFileText,
  FiDollarSign,
  FiArrowRight,
  FiCheck,
} from "@/lib/icons";
import { broker } from "@/config/broker.config";

interface ServiceCard {
  title: string;
  description: string;
  href: string;
  icon: React.ReactNode;
  features: string[];
}

const services: ServiceCard[] = [
  {
    title: "Asset Finance",
    description:
      "Finance equipment, vehicles, and machinery for your business. Keep capital free for operations while acquiring the assets you need to grow.",
    href: "/business-loans/asset-finance",
    icon: <FiTruck className="size-6" />,
    features: [
      "Vehicles & fleet",
      "Equipment & machinery",
      "Technology & IT",
    ],
  },
  {
    title: "Invoice Finance",
    description:
      "Unlock cash tied up in unpaid invoices. Improve cash flow without taking on traditional debt by borrowing against your receivables.",
    href: "/business-loans/invoice-finance",
    icon: <FiFileText className="size-6" />,
    features: [
      "Immediate cash flow",
      "No property security",
      "Flexible facility",
    ],
  },
  {
    title: "Term Loans",
    description:
      "Flexible business funding for growth, working capital, or specific projects. Structured repayments over a set term.",
    href: "/business-loans/term-loans",
    icon: <FiDollarSign className="size-6" />,
    features: [
      "Working capital",
      "Business expansion",
      "Debt consolidation",
    ],
  },
];

const benefits = [
  {
    title: "Multiple lender options",
    description:
      "We compare products from banks, specialist business lenders, and fintech providers to find the best fit.",
  },
  {
    title: "Fast turnaround",
    description:
      "Many business finance products can be approved within days, not weeks. We know which lenders move quickly.",
  },
  {
    title: "Flexible structures",
    description:
      "From chattel mortgages to operating leases, we'll find the structure that suits your tax and cash flow needs.",
  },
  {
    title: "Expert guidance",
    description:
      "Business finance can be complex. We explain your options clearly and help you make informed decisions.",
  },
];

const industries = [
  "Construction & trades",
  "Transport & logistics",
  "Manufacturing",
  "Healthcare & medical",
  "Professional services",
  "Retail & hospitality",
  "Agriculture",
  "Technology",
];

export function BusinessLoansPage() {
  return (
    <>
      <SEO
        title="Business Loans Brisbane, Ipswich & Springfield"
        description="Business loans for SMEs in Brisbane, Ipswich, Springfield & Logan. Asset finance, invoice finance and term loans compared from 70+ lenders."
        url="/business-loans"
        keywords={[
          "business loans Brisbane",
          "business loans Ipswich",
          "business loans Springfield",
          "business finance Brisbane",
          "SME loans Brisbane",
          "asset finance",
          "invoice finance",
        ]}
        jsonLd={[
          serviceSchema({
            name: "Business Loans",
            description:
              "Business finance for SMEs — asset finance, invoice finance, term loans and working capital from 70+ lenders.",
            url: "/business-loans",
            serviceType: "Business Loan",
          }),
          breadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Business Loans", url: "/business-loans" },
          ]),
        ]}
      />

      {/* Hero */}
      <section className="bg-cream py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="section-label mb-4">Business Finance</p>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6">
                Funding to grow your business
              </h1>
              <p className="text-lg text-muted-foreground mb-8">
                Whether you need to finance equipment, improve cash flow, or fund
                expansion, we'll find the right solution from our panel of business
                lenders.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full font-medium hover:bg-primary/90 transition-colors"
                >
                  Discuss Your Needs
                  <FiArrowRight className="size-4" />
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="bg-primary rounded-3xl p-8 text-primary-foreground">
                <p className="text-primary-foreground/90 text-sm uppercase tracking-wider mb-4">
                  We help businesses with
                </p>
                <ul className="space-y-3">
                  {[
                    "Equipment and vehicle purchases",
                    "Cash flow management",
                    "Working capital needs",
                    "Business expansion funding",
                    "Debt refinancing and consolidation",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <FiCheck className="size-5 text-primary-foreground flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">Our Services</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              Business finance solutions
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              We offer a range of business finance products to suit different needs.
              Not sure which is right for you? Get in touch and we'll help.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {services.map((service) => (
              <Link
                key={service.href}
                to={service.href}
                className="group bg-white rounded-2xl p-6 border border-border/50 hover:shadow-lg hover:border-primary/20 transition-all"
              >
                <div className="p-3 bg-cream rounded-xl inline-flex mb-4 group-hover:bg-accent/20 transition-colors">
                  <span className="text-primary">{service.icon}</span>
                </div>
                <h3 className="font-semibold text-xl mb-2 group-hover:text-primary transition-colors">
                  {service.title}
                </h3>
                <p className="text-muted-foreground text-sm mb-4">
                  {service.description}
                </p>
                <ul className="space-y-2 mb-4">
                  {service.features.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-center gap-2 text-sm text-muted-foreground"
                    >
                      <FiCheck className="size-4 text-primary flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <span className="inline-flex items-center gap-1 text-sm font-medium text-primary group-hover:gap-2 transition-all">
                  Learn more
                  <FiArrowRight className="size-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-24 bg-cream">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">Why Choose Us</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              Business finance made simple
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {benefits.map((benefit) => (
              <div
                key={benefit.title}
                className="bg-white rounded-2xl p-6 border border-border/50"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-accent/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <FiCheck className="size-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">{benefit.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      {benefit.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="section-label mb-4">Industries</p>
              <h2 className="font-serif text-3xl md:text-4xl mb-6">
                We work with businesses across all industries
              </h2>
              <p className="text-muted-foreground mb-8">
                From sole traders to established companies, we help businesses of
                all sizes access the finance they need. Our lender panel includes
                specialists for different industries and situations.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-full font-medium hover:bg-primary/90 transition-colors"
              >
                Discuss your needs
                <FiArrowRight className="size-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {industries.map((industry) => (
                <div
                  key={industry}
                  className="bg-muted/50 rounded-xl px-4 py-3 text-sm font-medium"
                >
                  {industry}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        label="Ready to grow?"
        title="Let's find the right finance for your business"
        description="Book a free consultation and we'll discuss your options."
        primaryAction={{ label: "Get Started", href: "/contact" }}
        secondaryAction={{
          label: "Call Us",
          href: `tel:${broker.phone.replace(/\s/g, "")}`,
        }}
      />
    </>
  );
}
